import type { PortalPayout, PortalStatement } from '@/types/api'

/**
 * The arithmetic behind Home, kept out of the components that draw it.
 *
 * All of it is a plain sum. A reversed payout carries the negative amount rather than
 * cancelling the row it corrects, which is what lets every figure downstream stay a sum
 * and none of them have to know what a reversal is - so a month that nets negative is a
 * real answer here, not a case to special-case away.
 *
 * Nothing in this file adds across currencies. Ringgit and Singapore dollars are grouped
 * and never summed: there are no FX rates in this system, and a partner's own money screen
 * is exactly where somebody would be tempted to invent the first one.
 */

/** One month of one currency. `amount` is net: reversals have already reduced it. */
export interface TrendMonth {
  /** `YYYY-MM`, for keys and ordering. */
  key: string
  /** Short month name, with the year on January so a twelve-month window reads unambiguously. */
  label: string
  amount: number
}

export interface CurrencyTrend {
  currencyCode: string
  months: TrendMonth[]
  /** The window's net total. Not the same as paid to date, which is every payout ever. */
  total: number
}

export interface CurrencyAmount {
  currencyCode: string
  amount: number
  count: number
}

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

/**
 * The calendar month a date string falls in, as `YYYY-MM`.
 *
 * Read off the string rather than through `new Date`. `paidOn` is a `DateOnly` - a
 * calendar day with no time and no zone - and `new Date('2026-01-01')` is parsed as UTC
 * midnight, which in any negative offset reads back as 31 December. A payout would land in
 * the wrong month for a reader in the Americas and nobody here would ever see it.
 */
export function monthKeyOf(paidOn: string): string | undefined {
  const match = /^(\d{4})-(\d{2})/.exec(paidOn)
  if (!match) return undefined

  return `${match[1]}-${match[2]}`
}

/** The `count` most recent calendar months ending with the one `today` falls in, oldest first. */
function windowOf(today: Date, count: number): TrendMonth[] {
  const months: TrendMonth[] = []

  for (let back = count - 1; back >= 0; back--) {
    const at = new Date(today.getFullYear(), today.getMonth() - back, 1)
    const year = at.getFullYear()
    const month = at.getMonth()
    const name = MONTH_NAMES[month] ?? ''

    months.push({
      key: `${year}-${String(month + 1).padStart(2, '0')}`,

      // The year rides on January only. Twelve labels each carrying a year is noise; a
      // window with no January at all is inside one year anyway.
      label: month === 0 ? `${name} ${String(year).slice(2)}` : name,
      amount: 0,
    })
  }

  return months
}

/**
 * Monthly payout totals, one series per currency, over a rolling window.
 *
 * A currency appears only if it was paid inside the window. A partner paid in Singapore
 * dollars three years ago and in ringgit since should not be shown a flat empty chart for
 * a relationship that has moved on.
 *
 * Ordered by what the window is worth, largest first, so the currency a partner mostly
 * deals in leads. Ties fall back to the code so the order never depends on input order.
 */
export function monthlyTrend(
  payouts: readonly PortalPayout[],
  today: Date,
  months = 12,
): CurrencyTrend[] {
  const first = windowOf(today, months)[0]?.key
  if (first === undefined) return []

  const byCurrency = new Map<string, Map<string, number>>()

  for (const payout of payouts) {
    const key = monthKeyOf(payout.paidOn)

    // Outside the window, or a date the API should never have sent. Either way it is not
    // this chart's business; paid-to-date below still counts it.
    if (key === undefined || key < first) continue

    const series = byCurrency.get(payout.currencyCode) ?? new Map<string, number>()
    series.set(key, (series.get(key) ?? 0) + payout.amount)
    byCurrency.set(payout.currencyCode, series)
  }

  const trends: CurrencyTrend[] = []

  for (const [currencyCode, series] of byCurrency) {
    const monthsOf = windowOf(today, months).map((month) => ({
      ...month,
      amount: series.get(month.key) ?? 0,
    }))

    trends.push({
      currencyCode,
      months: monthsOf,
      total: monthsOf.reduce((sum, month) => sum + month.amount, 0),
    })
  }

  return trends.sort(
    (a, b) => b.total - a.total || a.currencyCode.localeCompare(b.currencyCode),
  )
}

/**
 * Everything ever remitted, per currency, and how many payouts it took.
 *
 * The whole history rather than the window: "paid to date" answers a different question
 * from the chart above it, and a partner whose first year is off the left edge of the
 * chart has still been paid for it.
 *
 * A reversal counts as a payout in `count` as well as reducing `amount`. It is a thing
 * that happened, it is a row on the Payouts screen, and a count that quietly disagreed
 * with that list would be the figure somebody queries.
 */
export function paidToDate(payouts: readonly PortalPayout[]): CurrencyAmount[] {
  const totals = new Map<string, CurrencyAmount>()

  for (const payout of payouts) {
    const running = totals.get(payout.currencyCode) ?? {
      currencyCode: payout.currencyCode,
      amount: 0,
      count: 0,
    }

    running.amount += payout.amount
    running.count += 1
    totals.set(payout.currencyCode, running)
  }

  return [...totals.values()].sort(
    (a, b) => b.amount - a.amount || a.currencyCode.localeCompare(b.currencyCode),
  )
}

/**
 * What has been stated and not yet settled, per currency.
 *
 * Issued is the only status that means money is owed. Paid is settled, and there is no
 * third state - a statement whose payout was reversed goes back to Issued rather than to
 * some "attempted" limbo, so it is owed again and counted here again.
 */
export function awaitingPayout(statements: readonly PortalStatement[]): CurrencyAmount[] {
  const totals = new Map<string, CurrencyAmount>()

  for (const statement of statements) {
    if (statement.status !== 'Issued') continue

    const running = totals.get(statement.currencyCode) ?? {
      currencyCode: statement.currencyCode,
      amount: 0,
      count: 0,
    }

    running.amount += statement.totalAmount
    running.count += 1
    totals.set(statement.currencyCode, running)
  }

  return [...totals.values()].sort(
    (a, b) => b.amount - a.amount || a.currencyCode.localeCompare(b.currencyCode),
  )
}
